import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { FileText, Download, Search, Eye, Mail, Printer, Calendar, X, Sparkles } from "lucide-react";
import { formatDate, formatLabel } from "@/lib/utils";
// Payslip amounts can carry paise (e.g. LOP proration produces 5066.67), so the
// preview and PDF show 2 decimals — unlike the app-wide whole-rupee formatCurrency.
const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import {
  emailPayslipsWithPdfs,
  getPayslipEmailJob,
  getPayrollByMonthAndYear,
} from "@/services/payroll.service";
import { getSettings, getBankAccounts, fetchEmployees } from "@/services/adminService";
import { usePermission } from "@/contexts/RoleAccessContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis, PaginationPageSize } from "@/components/ui/pagination";

const getTotalLeaves = (payslip = {}) => {
  const explicitTotal = Number(
    payslip.total_leaves ??
      payslip.total_leave ??
      payslip.leave_days ??
      payslip.total_leave_days,
  );

  if (!Number.isNaN(explicitTotal) && explicitTotal > 0) {
    return explicitTotal;
  }

  const paidLeaves = Number(
    payslip.total_paid_leaves ?? payslip.paid_leaves ?? payslip.paid_leave_days,
  );
  const unpaidLeaves = Number(
    payslip.total_unpaid_leaves ??
      payslip.unpaid_leaves ??
      payslip.lop_days ??
      payslip.unpaid_leave_days,
  );

  const combinedLeaves =
    (Number.isNaN(paidLeaves) ? 0 : paidLeaves) +
    (Number.isNaN(unpaidLeaves) ? 0 : unpaidLeaves);

  return combinedLeaves > 0 ? combinedLeaves : 0;
};

// Unpaid leave (LOP) is applied by the backend as a *reduction of earnings*, not as a
// deduction line: `earnings`/`gross_pay` arrive already prorated, while `monthly_earnings`/
// `monthly_gross_pay` carry the full-month figures. On the payslip we show the full monthly
// salary under Earnings and surface the docked amount as an explicit "Loss of Pay" deduction,
// so the employee can see why they were paid less. Net Salary is unchanged either way:
//   monthlyGross - (lop + total_deductions) === gross_pay - total_deductions
const getPayslipBreakdown = (payslip = {}) => {
  const monthlyEarnings = payslip.monthly_earnings?.length
    ? payslip.monthly_earnings
    : payslip.earnings || [];

  const monthlyGross = Number(
    payslip.monthly_gross_pay ?? payslip.gross_pay ?? 0,
  );
  const paidGross = Number(payslip.gross_pay ?? 0);

  // Keep paise — rounding here dropped the decimals from Total Deductions / Net.
  const lopAmount = Math.max(+(monthlyGross - paidGross).toFixed(2), 0);
  const lopDays = Number(payslip.total_unpaid_leaves ?? payslip.lop_days ?? 0) || 0;

  return {
    earnings: monthlyEarnings,
    grossPay: monthlyGross,
    lopAmount,
    lopLabel: lopDays
      ? `Loss of Pay (${lopDays} ${lopDays === 1 ? "day" : "days"})`
      : "Loss of Pay",
    totalDeductions: Number(payslip.total_deductions || 0) + lopAmount,
  };
};

// `deductions.loan_emi` and the top-level `loan_deductions` are the same figure; the
// dedicated "Loan EMI" row renders it, so skip it here to avoid a duplicate line.
const isDuplicateLoanKey = (key) =>
  key === "loan_emi" || key === "loan_deductions";

const Payslips = () => {
  const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [payslips, setPayslips] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [previewPayslip, setPreviewPayslip] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedBank, setSelectedBank] = useState();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [employeeData, setEmployeesData] = useState();
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResults, setEmailResults] = useState([]);
  const [emailProgress, setEmailProgress] = useState({
    processed: 0,
    sent: 0,
    failed: 0,
    total: 0,
    phase: "idle",
  });
  const [emailJobId, setEmailJobId] = useState("");
  const [selectedForEmail, setSelectedForEmail] = useState([]);
  const [selectedForAdvice, setSelectedForAdvice] = useState([]);
  const { can } = usePermission();
  const { joinRoom, leaveRoom, subscribe } = useWebSocket();
  const { canGenerate, canSendEmail } = createCanHelpers(can);
  const canGenerateBankAdvice = canGenerate("payslips", "generate_bank_advice");
  const canEmailPayslips = canSendEmail("payslips", "email_send");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1); // reset to first page when page size changes
  };

  // Full-month earnings + explicit Loss of Pay line for the preview dialog.
  const previewBreakdown = previewPayslip
    ? getPayslipBreakdown(previewPayslip)
    : null;

  useEffect(() => {
    fetchCompanySettings();
    fetchBankAccountsList();
    // Default to the month/year passed via ?month=&year= from a completed
    // payroll run (the "View Payslips" button), else the current month. Query
    // params (unlike history state) survive the AdminRoute wrapper and refresh.
    const now = new Date();
    const navMonth = searchParams.get("month");
    const navYear = searchParams.get("year");
    setSelectedMonth(
      navMonth
        ? String(navMonth).padStart(2, "0")
        : String(now.getMonth() + 1).padStart(2, "0"),
    );
    setSelectedYear(navYear ? String(navYear) : String(now.getFullYear()));
  }, []);

  const fetchBankAccountsList = async () => {
    try {
      const res = await getBankAccounts();

      const uniqueBankNames = [
        ...new Set(res.data.bank_accounts.map((b) => b.bank_name)),
      ];
      const formatted = uniqueBankNames.map((bankName) => ({
        value: bankName,
        label: bankName,
        format: ".csv", // optional default
      }));

      setBankAccounts(formatted);

      if (formatted.length > 0) {
        setSelectedBank(formatted[0].value);
      }
    } catch (err) {
      if (!isAuthExpiryError(err)) {
        console.error("Failed to load bank accounts", err);
      }
    }
  };

  useEffect(() => {
    fetchEmployeeDetails();
    if (selectedMonth && selectedYear) {
      fetchPayslips();
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (!emailJobId) return undefined;

    let isCancelled = false;

    // Apply a job snapshot (from the initial fetch or a pushed socket update).
    const applyJob = (job) => {
      if (isCancelled || !job) return;

      setEmailResults(job.results || []);
      setEmailProgress((prev) => ({
        ...prev,
        processed: Number(job.processed || 0),
        sent: Number(job.successful || 0),
        failed: Number(job.failed || 0),
        total: Number(job.total || prev.total || 0),
        phase: job.status || prev.phase,
      }));

      if (
        ["completed", "completed_with_errors", "failed"].includes(job.status)
      ) {
        setEmailJobId("");
        setEmailSending(false);

        if (Number(job.successful || 0) > 0) {
          toast.success(
            `Successfully sent ${Number(job.successful || 0)} payslip email${Number(job.successful || 0) === 1 ? "" : "s"}`
          );
          fetchPayslips({ silent: true });
        }

        if (Number(job.failed || 0) > 0) {
          toast.warning(
            `${Number(job.failed || 0)} payslip email${Number(job.failed || 0) === 1 ? "" : "s"} failed`
          );
        }
      }
    };

    // 1) One catch-up fetch for the current state (covers fast jobs and the
    //    window before we join the room).
    (async () => {
      try {
        const job = await getPayslipEmailJob(emailJobId);
        applyJob(job);
      } catch (error) {
        if (isCancelled) return;
        console.error("Error fetching payslip email job:", error);
        setEmailJobId("");
        setEmailSending(false);
        toast.error(
          getErrorMessage(error, "Failed to fetch payslip email status")
        );
      }
    })();

    // 2) Live updates pushed over the socket (no polling).
    const room = `payslip-email-job:${emailJobId}`;
    joinRoom(room);
    const offProgress = subscribe("payslip_email_progress", (job) => {
      if (job && String(job.id) === String(emailJobId)) applyJob(job);
    });
    // Re-join the room if the socket reconnects mid-job.
    const offReconnect = subscribe("connect", () => joinRoom(room));

    return () => {
      isCancelled = true;
      leaveRoom(room);
      offProgress();
      offReconnect();
    };
  }, [emailJobId]);

  const fetchEmployeeDetails = async () => {
    try {
      const res = await fetchEmployees();
      setEmployeesData(res);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching employee details:", error);
        toast.error(getErrorMessage(error, "Failed to load employee data"));
      }
      setEmployeesData([]);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await getSettings();
      setCompanySettings(response.data.company_settings);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching company settings:", error);
      }
      // Use fallback settings if API fails
      setCompanySettings({
        company_name: "PayrollPro Company",
        address: "123 Business District, Mumbai, Maharashtra - 400001",
        phone: "+91 22 1234 5678",
        email: "hr@payrollpro.com",
      });
    }
  };

  const fetchPayslips = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const data = await getPayrollByMonthAndYear(
        selectedMonth,
        selectedYear,
      );

      const rows = data?.employees || [];

      if (rows.length === 0) {
        toast.info(
          `No payslips found for ${getMonthName(selectedMonth)} ${selectedYear}`,
        );
      }

      setPayslips(rows);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching payslips:", error);
        toast.error(getErrorMessage(error, "Error fetching payslips"));
      }
      setPayslips([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const getPayrollTypeBadge = (type) => {
    if (type === "termination")
      return <Badge variant="destructive">Final Settlement</Badge>;
    if (type === "one_time") return <Badge variant="secondary">One-Time</Badge>;
    return <Badge>Regular</Badge>;
  };

  const handleEmailAllPayslips = async (singleSelection = null) => {
    if (!canSendEmail("payslips", true)) return;
    if (!selectedMonth || !selectedYear) {
      toast.error("Please select month and year");
      return;
    }

    if (emailSending) {
      console.log("Email sending already in progress, blocking duplicate call");
      return;
    }

    // ----------------------------------------
    // Normalize selections (single OR bulk)
    // ----------------------------------------
    let selections = [];

    if (singleSelection) {
      if (!singleSelection.employee_id || !singleSelection.payroll_run_id) {
        toast.error("Invalid payslip selection");
        return;
      }

      selections = [
        {
          payroll_run_id: singleSelection.payroll_run_id,
          employee_id: singleSelection.employee_id,
        },
      ];
    } else {
      if (selectedForEmail.length === 0) {
        toast.error("No payslips selected");
        return;
      }

      selections = selectedForEmail.map(parseSelectionKey);
    }

    setShowEmailDialog(true);
    setEmailSending(true);
    setEmailResults([]);
    setEmailProgress({
      processed: 0,
      sent: 0,
      failed: 0,
      total: selections.length,
      phase: "preparing_pdfs",
    });
    setEmailJobId("");

    let jobQueued = false;

    try {
      // ----------------------------------------
      // Build company object (UNCHANGED)
      // ----------------------------------------
      const company = companySettings
        ? {
          name: companySettings.company_name || "Element Tree",
          address:
            companySettings.address ||
            "D-601, Raikar Chambers, off K.D. Marg, opp Neelkanth Gardens, Govandi (East), Mumbai - 400088, India",
          phone:
            companySettings?.company_admin?.phone ||
            companySettings?.phone ||
            "",
          email:
            companySettings?.company_admin?.email ||
            companySettings?.email ||
            "",
          logo_url: companySettings.logo_url || null,
        }
        : {
          name: "Element Tree",
          address:
            "D-601, Raikar Chambers, off K.D. Marg, opp Neelkanth Gardens, Govandi (East), Mumbai - 400088, India",
          phone: "+91 22 1234 5678",
          email: "hr@elementtree.co.in",
          logo_url: null,
        };

      // ----------------------------------------
      // Generate PDFs (parallel)
      // ----------------------------------------
      const pdfPromises = selections.map(async (sel) => {
        const payslip = payslips.find(
          (p) =>
            String(p.employee_id) === String(sel.employee_id) &&
            String(p.payroll_run_id) === String(sel.payroll_run_id),
        );

        if (!payslip) return null;

        const employee = filteredPayslips.find(
          (e) => String(e.employee_id) === String(sel.employee_id),
        );

        if (!employee || !employee.details?.email) {
          return null;
        }

        const enrichedPayslip = {
          ...payslip,
          company,
          period: `${monthNames[payslip.period_month]} ${payslip.period_year}`,
        };

        try {
          // Build lightweight HTML (no on-device rendering); the backend turns
          // this into the PDF. Uploaded as a small HTML file via FormData.
          const html = buildPayslipHtml(enrichedPayslip);
          const htmlBlob = new Blob([html], { type: "text/html" });

          setEmailProgress((prev) => ({
            ...prev,
            processed: prev.processed + 1,
          }));

          return {
            htmlBlob,
            filename: `${employee.details.name.replace(/ /g, "_")}_${monthNames[payslip.period_month]
              }_${payslip.period_year}.pdf`,
            employee_id: sel.employee_id,
            payroll_run_id: sel.payroll_run_id,
            employee_name: employee.details.name,
            employee_email: employee.details.email,
          };
        } catch (err) {
          console.error("Payslip HTML build failed:", err);
          return null;
        }
      });

      const pdfResults = (await Promise.all(pdfPromises)).filter(Boolean);

      if (pdfResults.length === 0) {
        toast.error("No valid payslips found to email");
        setEmailSending(false);
        return;
      }

      setEmailResults(
        pdfResults.map((result) => ({
          employee_id: result.employee_id,
          payroll_run_id: result.payroll_run_id,
          employee_name: result.employee_name,
          email: result.employee_email,
          status: "queued",
          error: "",
        })),
      );

      // ----------------------------------------
      // Build FormData (PDFs + selections)
      // ----------------------------------------
      const formData = new FormData();

      pdfResults.forEach((r) => {
        // Field is still named "pdfs" for backend compatibility, but now carries
        // the payslip HTML that the server renders into the final PDF.
        formData.append("pdfs", r.htmlBlob, r.filename);
      });

      formData.append(
        "selections",
        JSON.stringify(
          pdfResults.map((r) => ({
            payroll_run_id: r.payroll_run_id,
            employee_id: r.employee_id,
            employee_name: r.employee_name,
            employee_email: r.employee_email,
          })),
        ),
      );

      formData.append("month", Number(selectedMonth));
      formData.append("year", Number(selectedYear));

      const requestId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      formData.append("request_id", requestId);

      console.log("Sending payslip email request:", requestId);

      // ----------------------------------------
      // API CALL
      // ----------------------------------------
      const response = await emailPayslipsWithPdfs(formData);

      setEmailResults(response.results || []);
      setEmailProgress((prev) => ({
        ...prev,
        processed: Number(response.processed || 0),
        sent: Number(response.successful || 0),
        failed: Number(response.failed || 0),
        total: Number(response.total || pdfResults.length),
        phase: response.status || "queued",
      }));

      if (response.id) {
        jobQueued = true;
        setEmailJobId(response.id);
      } else {
        setEmailSending(false);
      }
    } catch (error) {
      console.error("Error sending payslips:", error);
      toast.error(error?.response?.data?.message || "Failed to send payslips");
      setEmailResults([]);
      setEmailJobId("");
    } finally {
      if (!jobQueued) {
        setEmailSending(false);
      }
    }
  };

  const toggleSelectForEmail = (payslip) => {
    const key = makeSelectionKey(payslip);

    /* ---------------------------------
     EMAIL SELECTION (unchanged)
  ----------------------------------*/
    setSelectedForEmail((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });

    /* ---------------------------------
     BANK ADVICE (EMPLOYEE-ENRICHED)
  ----------------------------------*/
    setSelectedForAdvice((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];

      // remove if already selected
      if (
        safePrev.some(
          (e) =>
            String(e.employee_id) === String(payslip.employee_id) &&
            String(e.payroll_run_id) === String(payslip.payroll_run_id),
        )
      ) {
        return safePrev.filter(
          (e) =>
            !(
              String(e.employee_id) === String(payslip.employee_id) &&
              String(e.payroll_run_id) === String(payslip.payroll_run_id)
            ),
        );
      }

      // get full employee details (OLD BEHAVIOR)
      const employee = employeeData?.find(
        (emp) => String(emp._id) === String(payslip.employee_id),
      );

      if (!employee) return safePrev;

      return [
        ...safePrev,
        {
          ...employee, // 👈 FULL EMPLOYEE OBJECT
          payroll_run_id: payslip.payroll_run_id,
          net_salary: payslip.net_pay, // 👈 PAYSLIP VALUE OVERRIDES
        },
      ];
    });
  };

  const toggleSelectAllForEmail = () => {
    const allKeys = payslips.map(makeSelectionKey);
    const isAllSelected = selectedForEmail.length === allKeys.length;

    if (isAllSelected) {
      setSelectedForEmail([]);
      setSelectedForAdvice([]);
      return;
    }

    /* ---------------------------------
     EMAIL KEYS
  ----------------------------------*/
    setSelectedForEmail(allKeys);

    /* ---------------------------------
     BANK ADVICE (EMPLOYEE-ENRICHED)
  ----------------------------------*/
    const enrichedAdvice = payslips
      .map((payslip) => {
        const employee = employeeData?.find(
          (emp) => String(emp._id) === String(payslip.employee_id),
        );

        if (!employee) return null;

        return {
          ...employee, // 👈 FULL EMPLOYEE OBJECT
          employee_id: payslip.employee_id,
          payroll_run_id: payslip.payroll_run_id,
          net_salary: payslip.net_pay, // 👈 FROM PAYSLIP
        };
      })
      .filter(Boolean);

    setSelectedForAdvice(enrichedAdvice);
  };

  const getMonthName = (month) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[parseInt(month) - 1];
  };

  const handlePreviewPayslip = (payslip) => {
    // Ensure company settings are available
    const company = {
      name: companySettings?.company_name || "",
      address:
        companySettings?.address || "",
      phone: companySettings?.company_admin?.phone || companySettings?.phone || "",
      email: companySettings?.company_admin?.email || companySettings?.email || "",
      logo_url: companySettings?.logo_url || null,
    };
    // Enrich stored payslip with company data and period for display
    const enrichedPayslip = {
      ...payslip,
      company: company,
      period: `${getMonthName(payslip.month)} ${payslip.year}`,
    };

    console.log("Preview Payslip Data:", enrichedPayslip);
    setPreviewPayslip(enrichedPayslip);
    setShowPreview(true);
  };

  const handleDownloadPayslip = async (payslip) => {
    try {
      // Ensure company settings are available
      const company = companySettings
        ? {
          name: companySettings.company_name || "Element Tree",
          address:
            companySettings.address ||
            "D-601, Raikar Chambers, off K.D. Marg, opp Neelkanth Gardens, Govandi (East), Mumbai - 400088, India",
          phone: companySettings?.company_admin?.phone || companySettings?.phone || "",
          email: companySettings?.company_admin?.email || companySettings?.email || "",
          logo_url: companySettings.logo_url || null,
        }
        : {
          name: "Element Tree",
          address:
            "D-601, Raikar Chambers, off K.D. Marg, opp Neelkanth Gardens, Govandi (East), Mumbai - 400088, India",
          phone: "+91 22 1234 5678",
          email: "hr@elementtree.co.in",
          logo_url: null,
        };

      // Enrich payslip with company data for PDF generation
      const enrichedPayslip = {
        ...payslip,
        company: company,
        period: `${getMonthName(payslip.month)} ${payslip.year}`,
      };

      await generatePDF(enrichedPayslip);
      toast.success(
        `Payslip downloaded for ${payslip.details?.name || payslip.employee?.name
        }`
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  // Builds the payslip HTML markup used for the emailed PDF. This is intentionally
  // cheap (string building only) — the heavy HTML->PDF rendering now happens on
  // the backend so low-powered devices don't freeze during bulk emailing.
  const buildPayslipHtml = (payslipData) => {
    const breakdown = getPayslipBreakdown(payslipData);

    return `
    <div style="padding: 20px; background: white;">
      
      <!-- Company Header -->
      <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; align-items: start;">
          <div style="display: flex; align-items: center;">
            ${payslipData.company.logo_url
        ? `<img src="${payslipData.company.logo_url}" style="max-width: 60px; height: 60px; margin-right: 15px; object-fit: contain" />`
        : ""
      }
            <div>
              <h1 style="font-size: 24px; font-weight: bold; color: #10b981; margin: 0;">${payslipData.company.name
      }</h1>
            <p
              style="
                font-size: 12px;
                color: #6b7280;
                max-width: 260px;
                line-height: 1.4;
                word-wrap: break-word;
                white-space: normal;
                display: block;
              "
            >
              ${payslipData.company.address}
            </p>

              <p style="font-size: 12px; color: #6b7280;">Phone: ${payslipData.company.phone
      } | Email: ${payslipData.company.email}</p>
            </div>
          </div>

          <div style="text-align: right;">
            <h2 style="font-size: 20px; font-weight: 600;">SALARY SLIP</h2>
            <p style="font-size: 12px; color: #6b7280;">For the month of ${getMonthName(
        selectedMonth
      )} ${selectedYear}</p>
          </div>
        </div>
      </div>

      <!-- Employee Details -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
        <div>
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px;">Employee Details</h3>
          <table style="width: 100%; font-size: 12px;">
            <tr><td style="padding: 2px 0; color: #6b7280;">Name:</td><td style="font-weight: 500;">${payslipData.details.name
      }</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Employee ID:</td><td style="font-weight: 500;">${payslipData.details.employee_id
      }</td></tr>
            ${payslipData.details.designation
        ? `
                  <tr>
                    <td style="padding: 2px 0; color: #6b7280;">Department:</td>
                    <td style="font-weight: 500;">
                      ${payslipData?.details?.department || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 2px 0; color: #6b7280;">Designation:</td>
                    <td style="font-weight: 500;">
                      ${payslipData?.details?.designation || "-"}
                    </td>
                </tr>
                `
        : ""
      }
            <tr>
              <td style="padding: 2px 0; color: #6b7280;">
                Date of Joining:
              </td>
              <td style="font-weight: 500;">
                ${formatDate(payslipData.details.date_of_joining)}
              </td>
            </tr>
          </table>
        </div>

        <div>
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px;">Bank Details</h3>
          <table style="width: 100%; font-size: 12px;">
            <tr><td style="padding: 2px 0; color: #6b7280;">PAN:</td><td style="font-weight: 500;">${payslipData.details.pan
      }</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Bank Account:</td><td style="font-weight: 500;">***${payslipData.details.bank_account.slice(
        -4
      )}</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">IFSC:</td><td style="font-weight: 500;">${payslipData.details.ifsc
      }</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Days Worked:</td><td style="font-weight: 500;">${payslipData.total_working_days || 30
      } of ${payslipData.total_calendar_days || 30}</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Total Unpaid Leaves:</td><td style="font-weight: 500;">${getTotalLeaves(
        payslipData,
      )}</td></tr>
          </table>
        </div>
      </div>

      <!-- Salary Breakdown -->
<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px;">
  <!-- Earnings -->
  <div style="background: #ecfdf5; padding: 15px; border-left: 4px solid #10b981; border-radius: 8px; min-height: 200px; display: flex; flex-direction: column;">
    <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #065f46;">Earnings</h3>
    <div style="flex: 1; overflow: visible;">
      <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
        ${breakdown.earnings
        .map(
          (item) => `
          <tr>
            <td style="padding: 4px 0; white-space: nowrap;">${item.name}:</td>
            <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
            item.amount
          )}</td>
          </tr>`
        )
        .join("")}
        <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
          <td style="padding: 10px 0 0 0; font-weight: 700; color: #065f46;">Gross Earnings:</td>
          <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #065f46;">
            ${formatCurrency(breakdown.grossPay)}
          </td>
        </tr>
      </table>
    </div>
  </div>

  <!-- Reimbursements -->
  ${
    payslipData.reimbursements && payslipData.reimbursements.length > 0
      ? `<div style="background: #eff6ff; padding: 15px; border-left: 4px solid #3b82f6; border-radius: 8px; min-height: 120px; display: flex; flex-direction: column;">
    <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #1e40af;">Reimbursements</h3>
    <div style="flex: 1; overflow: visible;">
      <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
        ${payslipData.reimbursements
          .map(
            (item) => `
          <tr>
            <td style="padding: 4px 0; white-space: nowrap; text-transform: capitalize;">${item.claim_type || "Reimbursement"}:</td>
            <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(item.amount)}</td>
          </tr>`
          )
          .join("")}
        <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
          <td style="padding: 10px 0 0 0; font-weight: 700; color: #1e40af;">Total Reimbursements:</td>
          <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #1e40af;">
            ${formatCurrency(payslipData.reimbursements.reduce((s, r) => s + Number(r.amount || 0), 0))}
          </td>
        </tr>
      </table>
    </div>
  </div>`
      : ""
  }

  <!-- Deductions -->
  <div style="background: #fef2f2; padding: 15px; border-left: 4px solid #dc2626; border-radius: 8px; min-height: 200px; display: flex; flex-direction: column;">
    <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #991b1b;">Deductions</h3>
    <div style="flex: 1; overflow: visible;">
      <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
        ${breakdown.lopAmount > 0
        ? `<tr>
              <td style="padding: 4px 0; white-space: nowrap;">${breakdown.lopLabel}:</td>
              <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
          breakdown.lopAmount
        )}</td>
            </tr>`
        : ""
      }
        ${payslipData.deductions_list
        .map(
          (item) => `
          <tr>
            <td style="padding: 4px 0; white-space: nowrap;">${item.name}:</td>
            <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
            item.amount
          )}</td>
          </tr>`
        )
        .join("")}
        ${Object.entries(payslipData.deductions || {})
        .map(([key, value]) => {
          if (!value || Number(value) === 0) return "";
          if (isDuplicateLoanKey(key)) return "";
          const label = key.replace(/_/g, " ").toUpperCase();
          return `
            <tr>
              <td style="padding: 4px 0; white-space: nowrap;">${label}:</td>
              <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
            value
          )}</td>
            </tr>`;
        })
        .join("")}
        ${payslipData.loan_deductions > 0
        ? `<tr>
              <td style="padding: 4px 0; white-space: nowrap;">Loan EMI:</td>
              <td style="padding: 4px 0; text-align: right;">${formatCurrency(
          payslipData.loan_deductions
        )}</td>
            </tr>`
        : ""
      }
        <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
          <td style="padding: 10px 0 0 0; font-weight: 700; color: #991b1b;">Total Deductions:</td>
          <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #991b1b;">
            ${formatCurrency(breakdown.totalDeductions)}
          </td>
        </tr>
      </table>
    </div>
  </div>
</div>

      <!-- Net Salary -->
      <div style="border-top: 2px solid #e5e7eb; margin-top: 20px; padding-top: 20px;">
        <div style="display: flex; justify-content: space-between;">
          <span style="font-size: 16px; font-weight: 600;">Net Salary:</span>
          <span style="font-size: 24px; font-weight: bold; color: #10b981;">${formatCurrency(
        payslipData.net_pay
      )}</span>
        </div>
        <p style="font-size: 12px; color: #6b7280; margin-top: 10px;">Generated on: ${formatDate(
        new Date()
      )}</p>
      </div>

    </div>
    `;
  };

  const generatePDF = async (payslipData) => {
    const breakdown = getPayslipBreakdown(payslipData);

    // Create a temporary div with the payslip content
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.width = "800px";
    tempDiv.style.padding = "20px";
    tempDiv.style.backgroundColor = "white";
    tempDiv.style.fontFamily = "Arial, sans-serif";

    tempDiv.innerHTML = `
    <div style="padding: 20px; background: white;">
      
      <!-- Company Header -->
      <div style="border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; align-items: start;">
          <div style="display: flex; align-items: center;">
            ${payslipData.company.logo_url
        ? `<img src="${payslipData.company.logo_url}" style="max-width: 60px; height: 60px; margin-right: 15px; object-fit: contain" />`
        : ""
      }
            <div>
              <h1 style="font-size: 24px; font-weight: bold; color: #10b981; margin: 0;">${payslipData.company.name
      }</h1>
              <p style="font-size: 12px; color: #6b7280;">${payslipData.company.address
      }</p>
              <p style="font-size: 12px; color: #6b7280;">Phone: ${payslipData.company.phone
      } | Email: ${payslipData.company.email}</p>
            </div>
          </div>

          <div style="text-align: right;">
            <h2 style="font-size: 20px; font-weight: 600;">SALARY SLIP</h2>
            <p style="font-size: 12px; color: #6b7280;">For the month of ${getMonthName(
        selectedMonth
      )} ${selectedYear}</p>
          </div>
        </div>
      </div>

      <!-- Employee Details -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
        <div>
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px;">Employee Details</h3>
          <table style="width: 100%; font-size: 12px;">
            <tr><td style="padding: 2px 0; color: #6b7280;">Name:</td><td style="font-weight: 500;">${payslipData.details.name
      }</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Employee ID:</td><td style="font-weight: 500;">${payslipData.details.employee_id
      }</td></tr>
            ${payslipData.details.designation
        ? `
                  <tr>
                    <td style="padding: 2px 0; color: #6b7280;">Department:</td>
                    <td style="font-weight: 500;">
                      ${payslipData?.details?.department || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 2px 0; color: #6b7280;">Designation:</td>
                    <td style="font-weight: 500;">
                      ${payslipData?.details?.designation || "-"}
                    </td>
                </tr>
                `
        : ""
      }
            <tr>
              <td style="padding: 2px 0; color: #6b7280;">
                Date of Joining:
              </td>
              <td style="font-weight: 500;">
                ${formatDate(payslipData.details.date_of_joining)}
              </td>
            </tr>

          </table>
        </div>

        <div>
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px;">Bank Details</h3>
          <table style="width: 100%; font-size: 12px;">
            <tr><td style="padding: 2px 0; color: #6b7280;">PAN:</td><td style="font-weight: 500;">${payslipData.details.pan
      }</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Bank Account:</td><td style="font-weight: 500;">***${payslipData.details.bank_account.slice(
        -4
      )}</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">IFSC:</td><td style="font-weight: 500;">${payslipData.details.ifsc
      }</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Days Worked:</td><td style="font-weight: 500;">${payslipData.total_working_days || 30
      } of ${payslipData.total_calendar_days || 30}</td></tr>
            <tr><td style="padding: 2px 0; color: #6b7280;">Total Unpaid Leaves:</td><td style="font-weight: 500;">${getTotalLeaves(
        payslipData,
      )}</td></tr>
          </table>
        </div>
      </div>

      <!-- Salary Breakdown -->
<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px;">
  <!-- Earnings -->
  <div style="background: #ecfdf5; padding: 15px; border-left: 4px solid #10b981; border-radius: 8px; min-height: 200px; display: flex; flex-direction: column;">
    <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #065f46;">Earnings</h3>
    <div style="flex: 1; overflow: visible;">
      <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
        ${breakdown.earnings
        .map(
          (item) => `
          <tr>
            <td style="padding: 4px 0; white-space: nowrap;">${item.name}:</td>
            <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
            item.amount
          )}</td>
          </tr>`
        )
        .join("")}
        <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
          <td style="padding: 10px 0 0 0; font-weight: 700; color: #065f46;">Gross Earnings:</td>
          <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #065f46;">
            ${formatCurrency(breakdown.grossPay)}
          </td>
        </tr>
      </table>
    </div>
  </div>

  <!-- Reimbursements -->
  ${
    payslipData.reimbursements && payslipData.reimbursements.length > 0
      ? `<div style="background: #eff6ff; padding: 15px; border-left: 4px solid #3b82f6; border-radius: 8px; min-height: 120px; display: flex; flex-direction: column;">
    <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #1e40af;">Reimbursements</h3>
    <div style="flex: 1; overflow: visible;">
      <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
        ${payslipData.reimbursements
          .map(
            (item) => `
          <tr>
            <td style="padding: 4px 0; white-space: nowrap; text-transform: capitalize;">${item.claim_type || "Reimbursement"}:</td>
            <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(item.amount)}</td>
          </tr>`
          )
          .join("")}
        <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
          <td style="padding: 10px 0 0 0; font-weight: 700; color: #1e40af;">Total Reimbursements:</td>
          <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #1e40af;">
            ${formatCurrency(payslipData.reimbursements.reduce((s, r) => s + Number(r.amount || 0), 0))}
          </td>
        </tr>
      </table>
    </div>
  </div>`
      : ""
  }

  <!-- Deductions -->
  <div style="background: #fef2f2; padding: 15px; border-left: 4px solid #dc2626; border-radius: 8px; min-height: 200px; display: flex; flex-direction: column;">
    <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 10px; color: #991b1b;">Deductions</h3>
    <div style="flex: 1; overflow: visible;">
      <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
        ${breakdown.lopAmount > 0
        ? `<tr>
              <td style="padding: 4px 0; white-space: nowrap;">${breakdown.lopLabel}:</td>
              <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
          breakdown.lopAmount
        )}</td>
            </tr>`
        : ""
      }
        ${payslipData.deductions_list
        .map(
          (item) => `
          <tr>
            <td style="padding: 4px 0; white-space: nowrap;">${item.name}:</td>
            <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
            item.amount
          )}</td>
          </tr>`
        )
        .join("")}
        ${Object.entries(payslipData.deductions || {})
        .map(([key, value]) => {
          if (!value || Number(value) === 0) return "";
          if (isDuplicateLoanKey(key)) return "";
          const label = key.replace(/_/g, " ").toUpperCase();
          return `
            <tr>
              <td style="padding: 4px 0; white-space: nowrap;">${label}:</td>
              <td style="padding: 4px 0; text-align: right; width: 100%;">${formatCurrency(
            value
          )}</td>
            </tr>`;
        })
        .join("")}
        ${payslipData.loan_deductions > 0
        ? `<tr>
              <td style="padding: 4px 0; white-space: nowrap;">Loan EMI:</td>
              <td style="padding: 4px 0; text-align: right;">${formatCurrency(
          payslipData.loan_deductions
        )}</td>
            </tr>`
        : ""
      }
        <tr style="border-top: 2px solid #e0e0e0; margin-top: 8px;">
          <td style="padding: 10px 0 0 0; font-weight: 700; color: #991b1b;">Total Deductions:</td>
          <td style="padding: 10px 0 0 0; text-align: right; font-weight: 700; color: #991b1b;">
            ${formatCurrency(breakdown.totalDeductions)}
          </td>
        </tr>
      </table>
    </div>
  </div>
</div>

      <!-- Net Salary -->
      <div style="border-top: 2px solid #e5e7eb; margin-top: 20px; padding-top: 20px;">
        <div style="display: flex; justify-content: space-between;">
          <span style="font-size: 16px; font-weight: 600;">Net Salary:</span>
          <span style="font-size: 24px; font-weight: bold; color: #10b981;">${formatCurrency(
        payslipData.net_pay
      )}</span>
        </div>
        <p style="font-size: 12px; color: #6b7280; margin-top: 10px;">Generated on: ${formatDate(
        new Date()
      )}</p>
      </div>

    </div>
    `;

    document.body.appendChild(tempDiv);

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
    });

    document.body.removeChild(tempDiv);

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(
      `Payslip_${payslipData.details.name}_${getMonthName(
        selectedMonth
      )}_${selectedYear}.pdf`
    );
  };

  const handleEmailPayslip = (payslip) => {
    toast.success(
      `Payslip emailed to ${payslip.employee_details?.email || payslip.employee?.email
      }`
    );
    // In a real app, this would send an email with the payslip
  };

  const handleBulkDownload = () => {
    const generatedPayslips = payslips.filter((p) => p.status === "generated");
    toast.success(`Downloading ${generatedPayslips.length} payslips`);
  };

  const handleBulkEmail = () => {
    const generatedPayslips = payslips.filter((p) => p.status === "generated");
    toast.success(`Emailing payslips to ${generatedPayslips.length} employees`);
  };

  const makeSelectionKey = (payslip) =>
    `${payslip.payroll_run_id}__${payslip.employee_id}`;

  const parseSelectionKey = (key) => {
    const [payroll_run_id, employee_id] = key.split("__");
    return { payroll_run_id, employee_id };
  };

  const filteredPayslips = payslips.filter((payslip) => {
    const employeeName = payslip.details?.name || payslip.employee?.name || "";
    const employeeId =
      payslip?.details?.code || payslip.employee?.employee_id || "";
    const matchesSearch =
      employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      typeFilter === "all" || (payslip.payroll_type || "regular") === typeFilter;
    return matchesSearch && matchesType;
  });

  const getNested = (obj, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);

  const getNetPayForEmployee = (employeeId) => {
    const payslip = payslips.find((p) => p.employee_id === employeeId);
    return payslip?.net_pay || 0;
  };

  const getPayslipForEmployee = (employeeId) => {
    return payslips.find((p) => p.employee_id === employeeId);
  };

  const handleDownload = async (selectedBank, selectedForAdvice) => {
    if (!canGenerate("payslips", true)) return;
      try {
        if (!selectedBank) {
          toast.error("Please select a bank");
          return;
        }

        if (
          !Array.isArray(selectedForAdvice) ||
          selectedForAdvice.length === 0
        ) {
          toast.error("No employees selected for bank advice");
          return;
        }
        // Normalize bank name
        const bankKey = selectedBank.toLowerCase().replace(/\s+/g, "_");

        // Fetch template file safely
        let templateUrl;
        try {
          const module = await import(`../assets/bank-templates/${bankKey}.xlsx`);
          templateUrl = module.default;
        } catch (e) {
          templateUrl = `${process.env.REACT_APP_ASSETS_URL}/bank-templates/${bankKey}.xlsx`;
        }
        
        const templateRes = await fetch(templateUrl);

        if (!templateRes.ok) {
          toast.error(`Template not available for ${selectedBank}`);
          return; // stop execution
        }

        const arrayBuffer = await templateRes.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const headers = rows[0];

        // Fetch mapping file safely
        let fieldMap;
        try {
          const mappingModule = await import(`../assets/bank-mappings/${bankKey}.json`);
          fieldMap = mappingModule.default || mappingModule;
        } catch (e) {
          const mappingUrl = `${process.env.REACT_APP_ASSETS_URL}/bank-mappings/${bankKey}.json`;
          const mappingRes = await fetch(mappingUrl);
          if (!mappingRes.ok) {
            toast.error(`Mapping file missing for ${selectedBank}`);
            return;
          }
          fieldMap = await mappingRes.json();
        }

        const formattedRows = selectedForAdvice.map((txn) => {
          const row = {};
          headers.forEach((header) => {
            const backendKey = fieldMap[header];
            row[header] = backendKey ? (getNested(txn, backendKey) ?? "") : "";
          });
          return row;
        });

        const newSheet = XLSX.utils.json_to_sheet(formattedRows, {
          header: headers,
        });

        const outWorkbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(outWorkbook, newSheet, "Bank");

        const fileName = `${bankKey}_Advice_${selectedMonth}.xlsx`;

        const excelBuffer = XLSX.write(outWorkbook, {
          bookType: "xlsx",
          type: "array",
        });

        const file = new File([excelBuffer], fileName, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // const formData = new FormData();
        // formData.append("adviceXls", file);
        // formData.append("payPeriod", selectedMonth);
        // formData.append(
        //   "totalAmount",
        //   selectedForAdvice.reduce((s, e) => s + Number(e.net_salary || 0), 0)
        // );

        // formData.append("employeeCount", selectedForAdvice.length);
        // formData.append("paymentStatus", "Unpaid");
        // formData.append("bankName", selectedBank);

        // await saveAdviceRecord(formData);

        XLSX.writeFile(outWorkbook, fileName);
      } catch (err) {
        console.error("Error generating excel:", err);
        toast.error("Unexpected error while generating file.");
      }
  };

  const totalItems = filteredPayslips.length;

  const getEmailProgressLabel = () => {
    switch (emailProgress.phase) {
      case "preparing_pdfs":
        return "Preparing PDFs...";
      case "queued":
        return "Queued for sending...";
      case "processing":
        return "Sending emails...";
      case "completed":
        return "Email sending completed";
      case "completed_with_errors":
        return "Completed with some failures";
      case "failed":
        return "Email sending failed";
      default:
        return "Sending emails...";
    }
  };

  const renderEmailResultStatus = (status) => {
    switch (status) {
      case "sent":
        return (
          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200 dark:border-green-800">
            Sent
          </Badge>
        );
      case "processing":
        return (
          <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border-blue-200 dark:border-blue-800">
            Sending
          </Badge>
        );
      case "queued":
        return (
          <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 border-amber-200 dark:border-amber-800">
            Queued
          </Badge>
        );
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">{formatLabel(status || "pending")}</Badge>;
    }
  };

  const getEmailResultDetails = (result) => {
    if (result?.status === "failed") {
      return result?.error || "Failed";
    }

    if (result?.status === "processing") {
      return "Email is being sent";
    }

    if (result?.status === "queued") {
      return "Waiting in queue";
    }

    return "Success";
  };
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedMonth, selectedYear, typeFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const filteredemployees = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPayslips.slice(start, start + pageSize);
  }, [filteredPayslips, page, pageSize]);

  const buildPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };
  if (loading) {
    return (
      <div className="space-y-6" data-testid="payslips-skeleton">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-12 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payslips">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Payslips
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Generate and manage employee payslips
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="bg-amber-50 border border-amber-200 px-4 py-2 rounded-lg">
            <p className="text-sm text-amber-700">
              💡 Generate payslips from <strong>Run Payroll</strong> page after
              processing payroll
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{payslips.length}</p>
                <p className="text-xs text-gray-500">Total Payslips</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-primary rounded-full"></div>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {payslips.filter((p) => p.is_generated).length}
                </p>
                <p className="text-xs text-gray-500">Generated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {payslips.filter((p) => p.is_emailed).length}
                </p>
                <p className="text-xs text-gray-500">Emailed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Calendar className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">
                  {getMonthName(selectedMonth)}
                </p>
                <p className="text-xs text-gray-500">{selectedYear}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Payslip Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by employee name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem
                      key={i + 1}
                      value={String(i + 1).padStart(2, "0")}
                    >
                      {getMonthName(String(i + 1).padStart(2, "0"))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-24">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => (
                    <SelectItem key={2024 + i} value={String(2024 + i)}>
                      {2024 + i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="termination">Final Settlement</SelectItem>
                  <SelectItem value="one_time">One-Time</SelectItem>
                </SelectContent>
              </Select>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            disabled={
                              !canGenerateBankAdvice ||
                              selectedForAdvice.length === 0
                            }
                            className="font-medium"
                            onClick={() => {
                              if (!canGenerate("payslips", true)) return;
                            }}
                          >
                            Generate Bank Advice
                          </Button>
                        </DialogTrigger>

                        <DialogContent className="sm:max-w-[480px] py-6">
                          <DialogHeader className="space-y-2">
                            <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                              Generate Bank Advice
                            </DialogTitle>

                            <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                              Generates a downloadable bank transfer instruction
                              sheet for salary payments for{" "}
                              <strong>
                                {getMonthName(selectedMonth)} {selectedYear}
                              </strong>
                            </DialogDescription>
                          </DialogHeader>

                          <div className="grid gap-6 mt-2">
                            {/* Bank Selector Section */}
                            <div className="space-y-1.5">
                              <Label
                                className="text-sm font-medium"
                                htmlFor="bank-account"
                              >
                                Select Bank
                              </Label>

                              <Select
                                value={selectedBank}
                                onValueChange={setSelectedBank}
                              >
                                <SelectTrigger
                                  id="bank-account"
                                  className="w-full rounded-md shadow-sm text-gray-700 dark:text-gray-300"
                                >
                                  <SelectValue placeholder="Choose bank" />
                                </SelectTrigger>

                                <SelectContent>
                                  {bankAccounts.map((bank) => (
                                    <SelectItem
                                      key={bank.value}
                                      value={bank.value}
                                    >
                                      {bank.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end pt-2 space-x-3">
                              <Button
                                onClick={() => {
                                  if (!canGenerate("payslips", true)) return;
                                  handleDownload(
                                    selectedBank,
                                    selectedForAdvice,
                                  );
                                }}
                                className="bg-primary hover:bg-primary/90"
                              >
                                Generate Advice
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </span>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!canSendEmail("payslips", true)) return;
                          if (!emailSending) {
                            handleEmailAllPayslips();
                          }
                        }}
                        disabled={selectedForEmail.length === 0 || emailSending}
                        variant="outline"
                        className={`border-green-600 text-green-600 hover:bg-green-50 disabled:opacity-50`}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email Selected ({selectedForEmail.length})
                      </Button>
                    </span>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Payslip Table */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="w-12 text-gray-700 dark:text-gray-300">
                    <Checkbox
                      checked={
                        selectedForEmail.length === payslips.length &&
                        payslips.length > 0
                      }
                      onCheckedChange={toggleSelectAllForEmail}
                    />
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Employee Details
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Gross Salary
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Net Salary
                  </TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Status
                  </TableHead>
                  <TableHead className="text-right text-gray-700 dark:text-gray-300">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayslips.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-gray-500 dark:text-gray-400"
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-lg font-medium">
                          No payslips found
                        </span>
                        <span className="text-sm mt-1">
                          There are no payslips generated for{" "}
                          {getMonthName(selectedMonth)} {selectedYear}.
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredemployees.map((payslip) => (
                    <TableRow
                      key={`${payslip.payroll_run_id}__${payslip.employee_id}`}
                      className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                      onClick={() => handlePreviewPayslip(payslip)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedForEmail.includes(
                            makeSelectionKey(payslip),
                          )}
                          onCheckedChange={() => toggleSelectForEmail(payslip)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Avatar>
                            <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900/30 text-primary dark:text-emerald-400">
                              {(
                                payslip.details?.name ||
                                payslip.employee?.name ||
                                "N/A"
                              )
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-200">
                              {payslip.details?.name || payslip.employee?.name}{" "}
                              (
                              {payslip.details?.code ||
                                payslip.employee?.employee_id}
                              )
                            </div>
                            {(payslip.details?.department ||
                              payslip.employee?.department) && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {payslip.details?.department ||
                                  payslip.employee?.department}{" "}
                                •{" "}
                                {payslip.details?.designation ||
                                  payslip.employee?.designation}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-800 dark:text-gray-300">
                        {formatCurrency(payslip.gross_pay)}
                      </TableCell>
                      <TableCell className="font-medium text-primary dark:text-emerald-400">
                        {formatCurrency(payslip.net_pay)}
                      </TableCell>
                      <TableCell>
                        {getPayrollTypeBadge(payslip.payroll_type)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {/* Generation Status */}
                          <div className="flex items-center gap-2">
                            {payslip.is_generated ? (
                              <div className="flex items-center text-primary dark:text-emerald-400 text-xs font-medium">
                                <Sparkles className="w-3 h-3 mr-1" /> Generated
                              </div>
                            ) : (
                              <div className="flex items-center text-gray-500 dark:text-gray-400 text-xs font-medium">
                                <X className="w-3 h-3 mr-1" /> Not Generated
                              </div>
                            )}
                          </div>

                          {/* Email Status */}
                          {payslip.is_emailed && (
                            <div className="flex items-center text-blue-400 text-[11px] font-medium">
                              <Mail className="w-3 h-3 mr-1" /> Sent
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 hover:bg-blue-100 hover:text-blue-600`}
                            onClick={() => {
                              handlePreviewPayslip(payslip);
                            }}
                            title="View Payslip"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-green-100 hover:text-green-600"
                            onClick={() => handleDownloadPayslip(payslip)}
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payslip Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Payslip Preview - {previewPayslip?.details?.name}
            </DialogTitle>
          </DialogHeader>
          {previewPayslip && (
            <div
              className="bg-white dark:bg-gray-900 p-8 rounded-lg shadow-sm border dark:border-gray-700"
              id="payslip-preview"
            >
              {/* Company Header */}
              <div className="border-b dark:border-gray-700 pb-6 mb-6">
                <div className="flex justify-between items-start">
                  {/* LEFT SECTION */}
                  <div className="flex items-start">
                    {/* Render logo ONLY if exists */}
                    {previewPayslip.company.logo_url && (
                      <img
                        src={previewPayslip.company.logo_url}
                        alt="Company Logo"
                        className="w-16 h-16 object-contain mr-4"
                      />
                    )}

                    {/* Company Details */}
                    <div>
                      <h1 className="text-2xl font-bold text-primary">
                        {previewPayslip.company.name}
                      </h1>

                      {previewPayslip.company.address && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-normal break-words leading-snug max-w-[350px]">
                          {previewPayslip.company.address}
                        </p>
                      )}

                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Phone:{" "}
                        {companySettings?.company_admin?.phone ||
                          companySettings?.phone ||
                          "N/A"}{" "}
                        | Email:{" "}
                        {companySettings?.company_admin?.email ||
                          companySettings?.email ||
                          "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* RIGHT SECTION */}
                  <div className="text-right">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      SALARY SLIP
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      For the month of {getMonthName(selectedMonth)}{" "}
                      {selectedYear}
                    </p>
                  </div>
                </div>
              </div>

              {/* Employee Details */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
                    Employee Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Name:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {previewPayslip.details.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Employee ID:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {previewPayslip.details.employee_id}
                      </span>
                    </div>
                    {previewPayslip.details.designation ? (
                      <>
                        {previewPayslip.details.designation && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Designation:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {previewPayslip.details.designation}
                            </span>
                          </div>
                        )}
                        {previewPayslip.details.department && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Department:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {previewPayslip.details.department}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Date of Joining:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatDate(previewPayslip.details.date_of_joining)}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
                    Bank Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        PAN:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {previewPayslip.details.pan}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Bank Account:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        ***
                        {previewPayslip.details.bank_account.slice(-4)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        IFSC Code:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {previewPayslip.details.ifsc}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Days Worked:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {previewPayslip.total_working_days || 30} of{" "}
                        {previewPayslip.total_calendar_days || 30}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Total Unpaid Leaves:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {getTotalLeaves(previewPayslip)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Salary Breakdown */}
              <div className="grid grid-cols-2 gap-6">
                {/* Earnings */}
                <div className="bg-emerald-50 dark:bg-primary/20 p-4 rounded-lg border-l-4 border-primary dark:border-primary">
                  <h3 className="font-semibold mb-3 text-primary">
                    💰 Earnings
                  </h3>

                  <div className="space-y-2 text-sm text-gray-900 dark:text-gray-100">
                    {previewBreakdown.earnings?.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{item.name}:</span>
                        <span>{formatCurrency(item.amount)}</span>
                      </div>
                    ))}

                    {/* Gross */}
                    <div className="flex justify-between border-t dark:border-gray-600 pt-2 font-semibold">
                      <span>Gross Earnings:</span>
                      <span>{formatCurrency(previewBreakdown.grossPay)}</span>
                    </div>
                  </div>
                </div>

                {/* Reimbursements */}
                {previewPayslip.reimbursements?.length > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 dark:border-blue-600">
                    <h3 className="font-semibold mb-3 text-blue-700 dark:text-blue-400">
                      🧾 Reimbursements
                    </h3>

                    <div className="space-y-2 text-sm text-gray-900 dark:text-gray-100">
                      {previewPayslip.reimbursements.map((item, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="capitalize">
                            {item.claim_type || "Reimbursement"}:
                          </span>
                          <span>{formatCurrency(item.amount)}</span>
                        </div>
                      ))}

                      <div className="flex justify-between border-t dark:border-gray-600 pt-2 font-semibold">
                        <span>Total Reimbursements:</span>
                        <span>
                          {formatCurrency(
                            previewPayslip.reimbursements.reduce(
                              (s, r) => s + Number(r.amount || 0),
                              0,
                            ),
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Deductions */}
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border-l-4 border-red-500 dark:border-red-600">
                  <h3 className="font-semibold mb-3 text-red-700 dark:text-red-400">
                    💸 Deductions
                  </h3>

                  <div className="space-y-2 text-sm text-gray-900 dark:text-gray-100">
                    {/* Loss of Pay (unpaid leave) */}
                    {previewBreakdown.lopAmount > 0 && (
                      <div className="flex justify-between">
                        <span>{previewBreakdown.lopLabel}:</span>
                        <span>
                          {formatCurrency(previewBreakdown.lopAmount)}
                        </span>
                      </div>
                    )}

                    {/* Manual deductions */}
                    {previewPayslip.deductions_list?.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{item.name}:</span>
                        <span>{formatCurrency(item.amount)}</span>
                      </div>
                    ))}

                    {/* Statutory deductions */}
                    {Object.entries(previewPayslip.deductions || {}).map(
                      ([key, value]) => {
                        if (isDuplicateLoanKey(key)) return null; // handled separately
                        if (Number(value) === 0) return null;

                        return (
                          <div key={key} className="flex justify-between">
                            <span>{formatLabel(key)}:</span>
                            <span>{formatCurrency(value)}</span>
                          </div>
                        );
                      },
                    )}

                    {/* Loan deductions */}
                    {previewPayslip.loan_deductions > 0 && (
                      <div className="flex justify-between">
                        <span>Loan EMI:</span>
                        <span>
                          {formatCurrency(previewPayslip.loan_deductions)}
                        </span>
                      </div>
                    )}

                    {/* Total */}
                    <div className="flex justify-between border-t dark:border-gray-600 pt-2 font-semibold">
                      <span>Total Deductions:</span>
                      <span>
                        {formatCurrency(previewBreakdown.totalDeductions)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Salary */}
              <div className="border-t dark:border-gray-700 mt-6 pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Net Salary:
                  </span>
                  <span className="text-2xl font-bold text-primary dark:text-emerald-400">
                    {formatCurrency(previewPayslip.net_pay)}
                  </span>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Generated on: {formatDate(new Date())}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-center space-x-4 mt-8 no-print">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPayslip(previewPayslip)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  disabled={!canEmailPayslips}
                  onClick={() => {
                    if (!canSendEmail("payslips", true)) return;
                    handleEmailAllPayslips({
                      employee_id: previewPayslip.employee_id,
                      payroll_run_id: previewPayslip.payroll_run_id,
                    });
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Payslip
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Status Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Payslips Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Progress Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-gray-200 dark:border-gray-700">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {emailProgress.total}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Total
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 dark:border-gray-700">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {emailProgress.sent}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Sent
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 dark:border-gray-700">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {emailProgress.failed}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Failed
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Progress Bar */}
            {emailProgress.total > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                  <span>{getEmailProgressLabel()}</span>
                  <span>
                    {emailProgress.processed} / {emailProgress.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-600 dark:bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        emailProgress.total > 0
                          ? (emailProgress.processed / emailProgress.total) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Email Results List */}
            {emailResults.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-200 dark:border-gray-700">
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Employee
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Email
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Status
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Details
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailResults.map((result, index) => (
                      <TableRow
                        key={index}
                        className="border-b border-gray-200 dark:border-gray-700"
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-200">
                              {result.employee_name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-800 dark:text-gray-300">
                          {result.email || "N/A"}
                        </TableCell>
                        <TableCell>
                          {renderEmailResultStatus(result.status)}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 dark:text-gray-400">
                          {getEmailResultDetails(result)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEmailDialog(false)}
                disabled={emailSending}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {payslips.length > 0 && (
        <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
          <PaginationPageSize value={pageSize} onValueChange={handlePageSizeChange} />
          <Pagination className="mx-0 justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page === 1}
                  className={page === 1 ? "pointer-events-none opacity-50" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                />
              </PaginationItem>

              {buildPages(page, totalPages).map((p, idx) => {
                if (p === "...") {
                  return (
                    <PaginationItem key={`e-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }

                return (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === page}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page === totalPages}
                  className={
                    page === totalPages ? "pointer-events-none opacity-50" : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Payslips;
